import { ref } from 'vue'
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  increment
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { TIME_SLOTS, TIME_SLOT_GROUPS, getSlotsByPeriod } from '@/utils/timeSlots'

/**
 * 인앱 광고 배너 관리 Composable
 *
 * 메인 배너와 별도의 광고 상품
 * 앱 내 여러 곳에 삽입되는 작은 카드형 배너
 *
 * 배너 상태:
 * - pending: 승인 대기
 * - approved: 승인됨 (노출중)
 * - rejected: 거절됨
 *
 * Firestore 컬렉션: adBanners
 */
export function useAdBanner() {
  const loading = ref(false)
  const error = ref(null)

  /**
   * 한국 시간 가져오기
   */
  const getKoreanTime = () => {
    const now = new Date()
    const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
    return koreaTime
  }

  /**
   * 현재 시간대에 활성화된 배너 목록 조회
   */
  const getActiveBanners = async () => {
    loading.value = true
    error.value = null

    try {
      const now = getKoreanTime()
      const currentHour = now.getHours()
      const currentMinutes = now.getMinutes()
      const currentTime = `${String(currentHour).padStart(2, '0')}:${String(currentMinutes).padStart(2, '0')}`

      // approved 상태인 배너 조회
      const q = query(
        collection(db, 'adBanners'),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)

      // 현재 시간대에 해당하는 배너만 필터링
      const banners = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(banner => {
          if (!banner.timeSlot) return false
          return currentTime >= banner.timeSlot.start && currentTime < banner.timeSlot.end
        })

      return banners
    } catch (err) {
      console.error('AdBanner 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 랜덤 배너 1개 가져오기 (캐싱 포함)
   */
  let cachedBanners = null
  let cacheTime = null
  const CACHE_DURATION = 5 * 60 * 1000 // 5분

  const getRandomBanner = async () => {
    const now = Date.now()

    // 캐시가 유효하면 캐시에서 반환
    if (cachedBanners && cacheTime && (now - cacheTime) < CACHE_DURATION) {
      if (cachedBanners.length === 0) return null
      const randomIndex = Math.floor(Math.random() * cachedBanners.length)
      return cachedBanners[randomIndex]
    }

    // 캐시 갱신
    cachedBanners = await getActiveBanners()
    cacheTime = now

    if (cachedBanners.length === 0) return null
    const randomIndex = Math.floor(Math.random() * cachedBanners.length)
    return cachedBanners[randomIndex]
  }

  /**
   * 클릭 수 증가
   */
  const incrementClick = async (bannerId) => {
    try {
      const bannerRef = doc(db, 'adBanners', bannerId)
      await updateDoc(bannerRef, {
        clicks: increment(1)
      })
    } catch (err) {
      console.error('클릭 수 증가 실패:', err)
    }
  }

  /**
   * 노출 수 증가
   */
  const incrementImpression = async (bannerId) => {
    try {
      const bannerRef = doc(db, 'adBanners', bannerId)
      await updateDoc(bannerRef, {
        impressions: increment(1)
      })
    } catch (err) {
      console.error('노출 수 증가 실패:', err)
    }
  }

  /**
   * AdBanner 신청 (파트너)
   */
  const createAdBanner = async (bannerData) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()

      const docRef = await addDoc(collection(db, 'adBanners'), {
        ...bannerData,
        status: 'pending',
        clicks: 0,
        impressions: 0,
        duration: '1week',
        rejectReason: '',
        createdAt: now,
        updatedAt: now
      })

      return docRef.id
    } catch (err) {
      console.error('AdBanner 신청 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 내 AdBanner 목록 조회 (파트너)
   */
  const getMyAdBanners = async (partnerId) => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'adBanners'),
        where('partnerId', '==', partnerId),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('내 AdBanner 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 전체 AdBanner 목록 조회 (어드민)
   */
  const getAllAdBanners = async (statusFilter = null) => {
    loading.value = true
    error.value = null

    try {
      let q
      if (statusFilter) {
        q = query(
          collection(db, 'adBanners'),
          where('status', '==', statusFilter),
          orderBy('createdAt', 'desc')
        )
      } else {
        q = query(
          collection(db, 'adBanners'),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('AdBanner 목록 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * AdBanner 상태 변경 (어드민)
   */
  const updateAdBannerStatus = async (bannerId, status, rejectReason = '') => {
    loading.value = true
    error.value = null

    try {
      const bannerRef = doc(db, 'adBanners', bannerId)
      const now = new Date().toISOString()

      const updateData = {
        status,
        updatedAt: now
      }

      if (status === 'approved') {
        updateData.approvedAt = now
      } else if (status === 'rejected') {
        updateData.rejectReason = rejectReason
        updateData.rejectedAt = now
      }

      await updateDoc(bannerRef, updateData)
      return true
    } catch (err) {
      console.error('AdBanner 상태 변경 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * AdBanner 취소/삭제 (파트너 - pending 상태만)
   */
  const cancelAdBanner = async (bannerId) => {
    loading.value = true
    error.value = null

    try {
      await deleteDoc(doc(db, 'adBanners', bannerId))
      return true
    } catch (err) {
      console.error('AdBanner 취소 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 기존 AdBanner 확인 (pending 또는 approved 상태)
   */
  const checkExistingAdBanner = async (partnerId) => {
    try {
      // pending 상태 확인
      const pendingQ = query(
        collection(db, 'adBanners'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'pending')
      )
      const pendingSnapshot = await getDocs(pendingQ)
      if (!pendingSnapshot.empty) return true

      // approved 상태 확인
      const approvedQ = query(
        collection(db, 'adBanners'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'approved')
      )
      const approvedSnapshot = await getDocs(approvedQ)
      return !approvedSnapshot.empty
    } catch (err) {
      console.error('AdBanner 확인 실패:', err)
      return false
    }
  }

  return {
    loading,
    error,
    TIME_SLOTS,
    TIME_SLOT_GROUPS,
    getSlotsByPeriod,
    getKoreanTime,
    getActiveBanners,
    getRandomBanner,
    incrementClick,
    incrementImpression,
    createAdBanner,
    getMyAdBanners,
    getAllAdBanners,
    updateAdBannerStatus,
    cancelAdBanner,
    checkExistingAdBanner
  }
}
